import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, Mail, KeyRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errMsg, logoUrl } from "@/lib/api";

export const PortalBrand = ({ branding, compact = false }) => (
  <span className={`inline-flex flex-col leading-tight ${compact ? "items-start" : "items-center"}`} data-testid="portal-brand">
    {branding?.has_logo ? <span className={compact ? "bg-white rounded-lg px-3 py-1" : ""}><img src={logoUrl("portal")} alt={branding.company_name} className={compact ? "h-8 object-contain" : "max-h-16 max-w-[280px] object-contain"} /></span>
      : <span className={`font-display font-bold ${compact ? "" : "text-xl"}`}>{branding?.company_name}</span>}
    {branding?.tagline && <span className="mt-1 text-[10px] uppercase tracking-[0.2em] text-emerald-600" data-testid="portal-tagline">{branding.tagline}</span>}
  </span>
);

export default function PortalLogin() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [mode, setMode] = useState("password");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [branding, setBranding] = useState(null);
  const used = useRef(false);

  useEffect(() => { api.get("/public/branding").then((r) => setBranding(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    const token = params.get("token");
    if (!token || used.current) return;
    used.current = true;
    setBusy(true);
    const next = params.get("next") || "";
    api.post("/portal/magic", { token }).then(() => navigate(next.startsWith("/portal/") ? next : "/portal", { replace: true }))
      .catch((e) => { setError(errMsg(e, "This link is invalid or has expired")); setBusy(false); });
  }, [params, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError(""); setMsg("");
    try {
      if (mode === "password") {
        await api.post("/portal/login", { email, password });
        navigate("/portal", { replace: true });
      } else {
        await api.post("/portal/request-link", { email });
        setMsg("If that email belongs to a client, a sign-in link is on its way. Check your inbox.");
      }
    } catch (err) { setError(errMsg(err, "Sign in failed")); } finally { setBusy(false); }
  };

  const tab = (m, label, Icon) => (
    <button type="button" data-testid={`portal-tab-${m}`} onClick={() => { setMode(m); setError(""); setMsg(""); }}
      className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-full text-sm transition-colors ${mode === m ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-900"}`}>
      <Icon className="h-4 w-4" />{label}
    </button>
  );

  return (
    <div className="min-h-screen bg-slate-100 text-slate-900 grid place-items-center px-4" data-testid="portal-login">
      <form onSubmit={submit} className="w-full max-w-sm bg-white rounded-2xl shadow-xl p-8 space-y-6">
        <div className="flex justify-center" data-testid="portal-login-logo"><PortalBrand branding={branding} /></div>
        <div className="text-center"><h1 className="font-display text-2xl font-bold tracking-tight">Client portal</h1><p className="text-sm text-slate-500 mt-1">Download your feasibility studies and receipts.</p></div>
        {params.get("token") && busy ? <p className="flex items-center gap-2 text-sm text-slate-600"><Loader2 className="h-4 w-4 animate-spin" />Signing you in…</p> : (
          <>
            <div className="flex gap-1 rounded-full bg-slate-100 p-1">{tab("password", "Password", KeyRound)}{tab("link", "Email me a link", Mail)}</div>
            <Input data-testid="portal-email" type="email" required placeholder="Your email" value={email} onChange={(e) => setEmail(e.target.value)} className="h-11 bg-white border-slate-300 text-slate-900" />
            {mode === "password" && <Input data-testid="portal-password" type="password" required placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} className="h-11 bg-white border-slate-300 text-slate-900" />}
            <Button data-testid="portal-submit" disabled={busy} className="w-full h-11 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold">
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}{mode === "password" ? "Sign in" : "Send sign-in link"}
            </Button>
            {mode === "password" && <p className="text-xs text-slate-500 text-center">First time? Use “Email me a link”, then set a password inside the portal.</p>}
          </>
        )}
        {msg && <p data-testid="portal-msg" className="text-sm text-emerald-700">{msg}</p>}
        {error && <p data-testid="portal-error" className="text-sm text-rose-600">{error}</p>}
      </form>
    </div>
  );
}
